// modelTiers.js — derive a Fast/Medium/High/Reasoning tier label for a model.
//
// design-uplift sprint E4 (Cursor parity: tier labels on the model picker).
// Apolo's model catalog (modelRegistry) is a SACRED byte-identical mirror with
// NO `tier` field — so, exactly like providerLogos.js / compareDefaults.js, this
// is a renderer-only derivation that IMPORTS the frozen registry read-only and
// NEVER edits it. The tier is a HINT derived from the entry's frozen fields:
//
//   • kind 'reasoning' (or a 'reasoning' capability) → "Reasoning"
//   • else by pricing.input (USD / 1M tokens, a capability proxy):
//       ≥ 8   → "High"     (flagships: Opus, GPT-5.5-pro, …)
//       ≥ 1.5 → "Medium"   (Sonnet, GPT-5, Gemini 3.x pro, …)
//       else  → "Fast"     (Haiku, mini/flash/lite, local, …)
//
// Pricing is the best capability proxy available in the frozen shape; it auto-
// tracks any registry change (no stale hardcoded model-id array to maintain).

import { findModel } from './modelRegistry.js';

export const TIERS = ['fast', 'medium', 'high', 'reasoning'];

export const TIER_LABELS = {
  fast: 'Fast',
  medium: 'Medium',
  high: 'High',
  reasoning: 'Reasoning',
};

/** Pure: derive a tier key from a registry ModelEntry (no lookup). */
export function tierFromEntry(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const caps = Array.isArray(entry.capabilities) ? entry.capabilities : [];
  if (entry.kind === 'reasoning' || caps.includes('reasoning')) return 'reasoning';
  const input = Number(entry.pricing?.input ?? 0);
  if (!Number.isFinite(input)) return 'fast';
  if (input >= 8) return 'high';
  if (input >= 1.5) return 'medium';
  return 'fast';
}

/** By id — read-only lookup of the SACRED registry. null if not a registry model. */
export function tierForModel(modelId) {
  return tierFromEntry(findModel(modelId));
}

/** Human label for a tier key (null when there is no tier). */
export function tierLabel(tier) {
  return TIER_LABELS[tier] || null;
}
